import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, CreditCard, Users, QrCode, Contact, Signature, BookOpen, LogOut, Menu, X, BarChart3, ScrollText, Bell, Cog } from "lucide-react";
import { Wallet } from "lucide-react";
import Logo from "@/components/Logo";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const toggle = async () => {
    const next = !open; setOpen(next);
    if (next && data.unread > 0) { await api.post("/notifications/read-all"); setData((d) => ({ ...d, unread: 0 })); }
  };
  const clearAll = async () => { await api.post("/notifications/clear"); setData({ items: [], unread: 0 }); };
  return (
    <div className="relative">
      <button onClick={toggle} data-testid="notif-bell" className="relative h-9 w-9 rounded-full flex items-center justify-center text-[#8B9BB4] hover:text-[#F6F1E4] hover:bg-[#142B5A] transition-colors">
        <Bell size={18} strokeWidth={1.5} />
        {data.unread > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-[#D4AF37] text-[#081426] text-[10px] font-bold flex items-center justify-center" data-testid="notif-count">{data.unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 iw-card p-0 z-50 max-h-96 overflow-y-auto" data-testid="notif-dropdown">
          <div className="p-4 border-b border-[#ffffff1a]"><p className="overline">Notifications</p></div>
          {data.items.length === 0 ? <p className="p-4 text-sm text-[#8B9BB4]">You're all caught up.</p> :
            data.items.slice(0, 8).map((n) => (
              <div key={n.id} className="p-4 border-b border-[#ffffff1a]">
                <p className="text-sm text-[#F6F1E4]">{n.message}</p>
                <span className="overline text-[10px]">{n.type.replace(/_/g, " ")}</span>
              </div>
            ))}
          <div className="p-3 flex items-center justify-between">
            <Link to="/app/notifications" onClick={() => setOpen(false)} className="text-xs text-[#D4AF37]" data-testid="notif-view-all">View all</Link>
            {data.items.length > 0 && <button onClick={clearAll} className="text-xs text-[#8B9BB4] hover:text-red-400 transition-colors" data-testid="notif-clear-all">Clear all</button>}
          </div>
        </div>
      )}
    </div>
  );
}

const links = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/cards", label: "My Cards", icon: CreditCard },
  { to: "/app/clients", label: "Client Accounts", icon: Users },
  { to: "/app/studio", label: "QR & NFC Studio", icon: QrCode },
  { to: "/app/leads", label: "Leads", icon: Contact },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/app/audit", label: "Audit Log", icon: ScrollText },
  { to: "/app/signature", label: "Email Signature", icon: Signature },
  { to: "/app/billing", label: "Billing", icon: Wallet },
  { to: "/app/guide", label: "Plans & Guide", icon: BookOpen },
];

export default function DashboardLayout({ children, title, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const navLinks = user?.role === "admin" ? [...links, { to: "/app/settings", label: "Settings", icon: Cog }] : links;

  const Side = (
    <aside className="w-64 shrink-0 bg-[#0B1D3A] border-r border-[#ffffff1a] flex flex-col h-full">
      <div className="p-5 border-b border-[#ffffff1a]">
        <button onClick={() => navigate("/app")} data-testid="sidebar-logo"><Logo size={30} /></button>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navLinks.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
            data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors border-l-2 ${
                isActive ? "text-[#D4AF37] bg-[#142B5A] border-[#D4AF37]" : "text-[#8B9BB4] border-transparent hover:text-[#F6F1E4] hover:bg-[#142B5A]/50"
              }`
            }>
            <l.icon size={18} strokeWidth={1.5} />
            {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-[#ffffff1a]">
        <div className="px-3 py-2 mb-2">
          <p className="text-sm text-[#F6F1E4] font-semibold truncate">{user?.name}</p>
          <p className="text-xs text-[#8B9BB4] truncate">{user?.email}</p>
          <span className="overline mt-1 inline-block">{user?.plan} plan</span>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#8B9BB4] hover:text-[#F6F1E4] hover:bg-[#142B5A]/50 transition-colors">
          <LogOut size={18} strokeWidth={1.5} /> Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-[#081426]">
      <div className="hidden lg:block h-full">{Side}</div>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Side}</div>
        </div>
      )}
      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="glass px-5 py-4 flex items-center justify-between gap-4 shrink-0">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-[#F6F1E4]" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
            <h1 className="font-display text-xl md:text-2xl font-bold text-[#F6F1E4]" data-testid="page-title">{title}</h1>
          </div>
          <div className="flex items-center gap-3"><NotificationBell />{actions}</div>
        </header>
        <div className="flex-1 overflow-y-auto p-5 md:p-8">{children}</div>
      </main>
    </div>
  );
}
